import Colors from '@root/brand/src/utils/colors';
import CookieStorageService from '@root/core/src/services/cookie-storage-service';
import PropTypes from '@root/vendor/prop-types';
import QuotesContext from '@root/quotes/src/models/quotes-context';
import Responsive from '@root/core/src/utils/responsive';
import SceneContent from '@root/core/src/components/scene-content';
import SceneWrapper from '@root/core/src/components/scene-wrapper';
import avatarHeadsetImage from '@root/quotes/src/assets/avatar-headset/avatar-headset.png';
import avatarHeadsetImage2x from '@root/quotes/src/assets/avatar-headset/avatar-headset@2x.png';
import avatarHeadsetImage3x from '@root/quotes/src/assets/avatar-headset/avatar-headset@3x.png';
import useAnalytics from '@root/core/src/hooks/use-analytics';
import { Button } from '@root/core/src/components/button';
import { SUPPORT_PHONE } from '@root/core/src/models/phone';
import { StyleSheet, Theme } from '@root/core/src/utils/styles';
import { UNDERWRITING_PENDING_VIEWED_COOKIE } from '@root/quotes/src/components/underwriting-pending-layout';
import { formatPhoneNumberWithParentheses } from '@root/core/src/utils/strings';

export default function UnderwritingManualReviewLayout({
  quotesContext,
  analyticsContext,
}) {
  useAnalytics(`${analyticsContext}_MANUAL_REVIEW`);

  const manualReviewCondition = quotesContext?.underwritingDecision?.conditions?.find((condition) => condition.code === 'MANUAL_INTERVENTION_REQUIRED');
  const phoneNumber = manualReviewCondition?.data?.phoneNumber || SUPPORT_PHONE;

  const hasViewedPendingScene = CookieStorageService.get(UNDERWRITING_PENDING_VIEWED_COOKIE);

  return (
    <SceneWrapper>
      <SceneContent>
        <div css={styles.imageWrapper}>
          <img
            alt={'review in progress'}
            css={styles.image}
            src={avatarHeadsetImage}
            srcSet={`${avatarHeadsetImage, avatarHeadsetImage2x, avatarHeadsetImage3x}`}
          />
        </div>
        {hasViewedPendingScene && (
          <h5 css={styles.secondaryText}>
            {'Welcome back!'}
          </h5>
        )}
        <h5 css={styles.heading}>
          {'Call us to get your final quote.'}
        </h5>
        <p css={styles.bodyText}>
          {'We need to verify a few details before we can provide your final quote. This step can’t be completed online, so our customer service team will help you finish by phone.'}
        </p>
        <p css={styles.bodyText}>
          {'Call '}
          <a
            css={styles.phoneLink}
            href={`tel:${phoneNumber}`}
          >
            {formatPhoneNumberWithParentheses(phoneNumber)}
          </a>
          {' from Monday-Friday, 9 a.m. to 8 p.m. ET'}
        </p>
        <div css={styles.buttonWrapper}>
          <Button
            passProps={{ href: `tel:${phoneNumber}` }}
            tag={'a'}
          >
            {'Call customer service'}
          </Button>
        </div>
      </SceneContent>
    </SceneWrapper>
  );
}

UnderwritingManualReviewLayout.propTypes = {
  analyticsContext: PropTypes.string.isRequired,
  quotesContext: PropTypes.instanceOf(QuotesContext).isRequired,
};

const styles = StyleSheet.create({
  heading: {
    ...Theme.displayHeading3(),
    textAlign: 'center',
    marginBottom: 30,
  },
  primaryText: {
    ...Theme.heading3(),
    fontSize: 20,
    textAlign: 'center',
  },
  secondaryText: {
    ...Theme.heading4(),
    color: Colors.rootOrange(),
    textAlign: 'center',
  },
  bodyText: {
    ...Theme.paragraph1(),
    textAlign: 'center',
  },
  phoneLink: {
    ...Theme.paragraph1(),
    textDecoration: 'underline',
  },
  buttonWrapper: {
    display: 'flex',
    justifyContent: 'center',
    ...Responsive.sm({
      display: 'none',
    }),
  },
  imageWrapper: {
    display: 'flex',
    justifyContent: 'center',
    marginBottom: 40,
  },
  image: {
    width: 200,
  },
});
